import { IconButton, makeStyles } from '@material-ui/core'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { Button, PageTitle } from '../root/components/common'
import { UserPermissions } from 'web/src/services/permissions'
import { SettingsCreateSelectModal } from './components/location-settings/settings-create-select-modal'
import { LocationBaitSettingsCreateModal } from './components/location-settings/location-bait-settings-create-modal'
import {
    confirmLocationResetBaitThreshold,
    createLocationSettings,
    getLocationSettings,
    updateLocationSettings
} from './api/location-settings-api'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { enqueueSnackbar } from '../root/actions'
import { BaitSettings } from '../root/components/common/bait-settings'
import { Trash2Icon } from 'lucide-react'
import { LocationBaitSettingsDeleteModal } from './components/location-settings/location-bait-settings-delete-modal'

export const SETTING_TYPES = {
    BAIT_SETTINGS: 'bait_settings'
}

const useStyles = makeStyles(theme => ({
    wrapper: {
        maxWidth: 1200
    },
    chartWrapper: {},
    titleCheckboxes: {
        display: 'flex'
    },
    pageTitle: {
        justifyContent: 'space-between'
    }
}))

function LocationSettings(props) {
    const dispatch = useDispatch()
    const queryClient = useQueryClient()
    const classes = useStyles()
    const locationId = props.match.params.locationId

    const theme = useSelector(state => state.main.theme)
    const locale = useSelector(state => state.main.locale)
    const location = useSelector(state => state.locations.location)

    const { data: locationSettings, isLoading, refetch } = useQuery(
        ['location-settings', locationId],
        () => getLocationSettings(locationId),
        {
            refetchOnWindowFocus: false,
            refetchInterval: false,
            enabled: !!locationId,
            onError: () => {
                dispatch(
                    enqueueSnackbar({
                        message: locale.sagasMsgs.commonLoadingError,
                        options: {
                            variant: 'error'
                        }
                    })
                )
            }
        }
    )

    const { mutate: createMutation } = useMutation(
        data => createLocationSettings(data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['location-settings', locationId])
                dispatch(
                    enqueueSnackbar({
                        message: locale.common.success,
                        options: {
                            variant: 'success'
                        }
                    })
                )
            },
            onError: () => {
                dispatch(
                    enqueueSnackbar({
                        message: locale.sagasMsgs.commonLoadingError,
                        options: {
                            variant: 'error'
                        }
                    })
                )
            }
        }
    )

    const { mutate: updateMutation } = useMutation(
        data => updateLocationSettings(locationId, data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['location-settings', locationId])
                dispatch(
                    enqueueSnackbar({
                        message: locale.common.success,
                        options: {
                            variant: 'success'
                        }
                    })
                )
            },
            onError: () => {
                dispatch(
                    enqueueSnackbar({
                        message: locale.sagasMsgs.commonLoadingError,
                        options: {
                            variant: 'error'
                        }
                    })
                )
            }
        }
    )

    const canUpdate = useMemo(() => {
        return UserPermissions.locationV2(
            location?.is_assigned,
            location?.customer_id
        ).update
    }, [location])

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [currentSettings, setCurrentSettings] = useState({})
    const [
        isSettingsCreateSelectModalOpen,
        setIsSettingsCreateSelectModalOpen
    ] = useState(false)
    const [
        isBaitSettingsCreateModalOpen,
        setIsBaitSettingsCreateModalOpen
    ] = useState(false)

    const availableSettingTypes = [
        {
            type: SETTING_TYPES.BAIT_SETTINGS,
            label: locale.locations.baitSettings,
            isAdded: !!currentSettings?.bait_settings
        }
    ]

    const canAddMoreSettings = availableSettingTypes.some(st => !st.isAdded)
    const hasAnySettings = !!currentSettings?.bait_settings

    const handleAddSetting = settingType => {
        setIsSettingsCreateSelectModalOpen(false)
        if (settingType === SETTING_TYPES.BAIT_SETTINGS) {
            setIsBaitSettingsCreateModalOpen(true)
        }
    }

    const handleRemoveSetting = settingType => {
        const newSettings = { ...currentSettings }
        delete newSettings[settingType]
        setIsDeleteModalOpen(false)
        updateMutation(newSettings)
        setCurrentSettings(newSettings)
    }

    const handleBaitSettingsCreate = useCallback(
        async baitSettings => {
            try {
                await confirmLocationResetBaitThreshold(locationId)
                const newSettings = { ...currentSettings }
                newSettings.bait_settings = baitSettings
                setCurrentSettings(newSettings)
                createMutation({
                    location_id: locationId,
                    data: newSettings
                })
            } catch (ex) {
                dispatch(
                    enqueueSnackbar({
                        message: locale.sagasMsgs.commonLoadingError,
                        options: {
                            variant: 'error'
                        }
                    })
                )
            }
        },
        [createMutation, locationId, currentSettings]
    )

    const handleUpdateDataPortionOfBaitSettings = data => {
        const newSettings = { ...currentSettings }
        newSettings.bait_settings = { ...newSettings.bait_settings, ...data }
        setCurrentSettings(newSettings)
        updateMutation(newSettings)
    }

    useEffect(() => {
        if (locationSettings?.data) {
            setCurrentSettings(locationSettings.data)
        }
    }, [locationSettings])

    return (
        <div className={classes.wrapper}>
            <PageTitle
                title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div>{locale.locations.locationSettings}</div>
                        <div
                            style={{
                                color: theme.textPrimaryReverted,
                                fontSize: 14,
                                fontWeight: 600,
                                marginLeft: 10,
                                backgroundColor: `${theme.active}94`,
                                padding: '2px 8px',
                                borderRadius: theme.borderRadius
                            }}
                        >
                            Beta
                        </div>
                    </div>
                }
                className={classes.pageTitle}
            />

            <div style={{ width: '100%' }}>
                {!hasAnySettings ? (
                    <div>
                        <div style={{ fontSize: 14 }}>
                            {locale.locations.noSettingsConfiguredYet}
                        </div>
                        {canUpdate && canAddMoreSettings ? (
                            <Button
                                variant="text"
                                style={{ marginTop: 20 }}
                                onClick={() => {
                                    setIsSettingsCreateSelectModalOpen(true)
                                }}
                            >
                                + {locale.locations.add}
                            </Button>
                        ) : null}
                    </div>
                ) : (
                    <>
                        {currentSettings.bait_settings ? (
                            <div>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: 20,
                                        marginTop: 20
                                    }}
                                >
                                    <div style={{ fontSize: 20 }}>
                                        {locale.locations.baitSettings}
                                    </div>
                                    {canUpdate ? (
                                        <IconButton
                                            onClick={() =>
                                                setIsDeleteModalOpen(true)
                                            }
                                        >
                                            <Trash2Icon
                                                size={24}
                                                color={theme.error}
                                            />
                                        </IconButton>
                                    ) : null}
                                </div>
                                <BaitSettings
                                    data={currentSettings.bait_settings}
                                    updateData={
                                        handleUpdateDataPortionOfBaitSettings
                                    }
                                    canUpdate={canUpdate}
                                    isDevice={false}
                                    isLocationSettings={true}
                                />
                            </div>
                        ) : null}
                    </>
                )}
            </div>

            <SettingsCreateSelectModal
                availableSettingTypes={availableSettingTypes}
                onClose={() => setIsSettingsCreateSelectModalOpen(false)}
                open={isSettingsCreateSelectModalOpen}
                onSelectSettingType={handleAddSetting}
            />

            <LocationBaitSettingsCreateModal
                open={isBaitSettingsCreateModalOpen}
                onClose={() => setIsBaitSettingsCreateModalOpen(false)}
                onSave={handleBaitSettingsCreate}
            />

            <LocationBaitSettingsDeleteModal
                open={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirmClick={() =>
                    handleRemoveSetting(SETTING_TYPES.BAIT_SETTINGS)
                }
            />
        </div>
    )
}

export default withRouter(LocationSettings)
